import type { LatLngBounds } from "leaflet";
import _ from "lodash";
import { type RefObject, useCallback, useEffect, useRef } from "react";

import type Light from "@/models/light";
import type { BoxSelectorHandle, MapHandle, MapMode } from "@/types";

interface BoxCreatedEvent {
    layer: {
        _bounds: LatLngBounds;
    };
}

interface Tool {
    name: string;
}

/**
 * Props for the `useBoxSelector` hook.
 */
interface BoxSelectorProps {
    /**
     * Callback to deselect lights.
     */
    deselectLights: (lights: Light[]) => void;
    /**
     * Whether the active mode forbids box selection entirely.
     */
    disabled: boolean;
    /**
     * The ref holding the imperative handle of the `Map` component.
     */
    mapRef: RefObject<MapHandle | null>;
    /**
     * The active map mode.
     */
    mode: MapMode;
    /**
     * The mode-scoped lights the box selects against.
     */
    modeLights: Light[];
    /**
     * Whether paste mode is active.
     */
    pasteModeActivated: boolean;
    /**
     * The currently selected drawing tool.
     */
    selectedTool: Tool | null;
    /**
     * Callback to select lights.
     */
    selectLights: (lights: Light | Light[], selectionType?: string) => void;
    /**
     * Callback to set the selected tool.
     */
    setSelectedTool: (tool: Tool | null) => void;
}

/**
 * Wires the rectangle box selector: additive and subtractive selection plus the
 * effects that enable or disable the control as the mode and tool change.
 *
 * @param params - The map handle ref, mode and tool state, and selection callbacks.
 * @returns The box selector ref and the handler to pass to `onCreated`.
 */
export const useBoxSelector = ({
    deselectLights,
    disabled,
    mapRef,
    mode,
    modeLights,
    pasteModeActivated,
    selectedTool,
    selectLights,
    setSelectedTool,
}: BoxSelectorProps) => {
    const boxSelectorRef = useRef<BoxSelectorHandle | null>(null);

    const getLightsInBounds = useCallback(
        ({ layer }: BoxCreatedEvent) =>
            modeLights.filter((light) => layer._bounds.overlaps(light.bounds)),
        [modeLights],
    );

    const handleRectangleSelection = useCallback(
        (e: BoxCreatedEvent) => {
            const selectedLightsInBounds = getLightsInBounds(e);

            selectLights(selectedLightsInBounds, "multiple");

            if (selectedLightsInBounds.length > 0 && boxSelectorRef.current) {
                boxSelectorRef.current.disable();
            }
        },
        [getLightsInBounds, selectLights],
    );

    const handleSubtractSelection = useCallback(
        (e: BoxCreatedEvent) => {
            const lightsInBounds = getLightsInBounds(e);

            deselectLights(lightsInBounds);

            if (boxSelectorRef.current) {
                boxSelectorRef.current.disable();
            }

            setSelectedTool(null);
        },
        [getLightsInBounds, deselectLights, setSelectedTool],
    );

    useEffect(() => {
        if (!mapRef.current) return;

        const boxSelector = boxSelectorRef.current;

        const isLightSelected = !_.some(modeLights, ["selected", true]);
        const isSubtractToolEnabled = selectedTool?.name === "subtract";
        const isSelectModeEnabled = mode.name === "select";
        const isRectangleSelectEnabled = mode.props.type === "Rectangle";

        const shouldEnableBoxSelector =
            (isLightSelected || isSubtractToolEnabled) &&
            isSelectModeEnabled &&
            (isRectangleSelectEnabled || isSubtractToolEnabled) &&
            boxSelector &&
            !boxSelector.enabled();

        if (pasteModeActivated && boxSelector) {
            boxSelector.disable();
        } else if (shouldEnableBoxSelector) {
            boxSelector.enable();
        }
    }, [
        mapRef,
        mode.name,
        mode.props.type,
        modeLights,
        pasteModeActivated,
        selectedTool?.name,
    ]);

    useEffect(() => {
        if (disabled && boxSelectorRef.current?.enabled()) {
            boxSelectorRef.current.disable();
        }
    }, [disabled]);

    const onBoxCreated =
        selectedTool?.name === "subtract"
            ? handleSubtractSelection
            : handleRectangleSelection;

    return { boxSelectorRef, onBoxCreated };
};
